<template>
  <nav v-if="!hideOnSinglePage || _pageCount > 1" class="v-pagination">
    <ul v-if="layout.includes('pager')" class="v-pager">
      <!-- Arrow left -->
      <li v-if="layout.includes('arrows')" class="v-pagination__arrow" :class="{ 'is-disabled': modelValue <= 1 }" @click="previous">
        <VIcon icon="ChevronLeft" :size="14" />
      </li>

      <!-- First and ... pages -->
      <template v-if="pagesInRange[0] !== 1">
        <li @click="model = 1">1</li>
        <li v-if="pagesInRange[0] !== 2" class="v-pagination__dots" @click="model = pagesInRange[0] - 1">...</li>
      </template>

      <!-- Pages in current range -->
      <li v-for="(page, index) in pagesInRange" :key="index" :class="{ 'is-active': page === modelValue }" @click="model = page" v-text="page" />

      <!-- Last and ... pages -->
      <template v-if="lastInRange < _pageCount">
        <li v-if="lastInRange + 1 < _pageCount" class="v-pagination__dots" @click="model = lastInRange + 1">...</li>
        <li @click="model = _pageCount" v-text="_pageCount" />
      </template>

      <!-- Arrow right -->
      <li v-if="layout.includes('arrows')" class="v-pagination__arrow" :class="{ 'is-disabled': modelValue >= _pageCount }" @click="next">
        <VIcon icon="ChevronRight" :size="14" />
      </li>
    </ul>

    <VSelect v-if="layout.includes('sizes')" v-model="pageSize" class="v-popper--pagination-sizes">
      <VOption v-for="option in pageSizes" :key="option" :label="`${option} / page`" :value="option" />
    </VSelect>
  </nav>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

import type { VPaginationLayout } from '../types.ts'
import VIcon from './Icon/VIcon.vue'
import VOption from './Select/VOption.vue'
import VSelect from './Select/VSelect.vue'

export interface Props {
  pageCount?: number
  pagerCount?: number
  total?: number
  pageSizes?: number[]
  layout?: VPaginationLayout[]
  hideOnSinglePage?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  pageCount: undefined,
  pagerCount: 3,
  total: 0,
  pageSizes: () => [10, 20, 30],
  layout: () => ['pager', 'arrows'],
  hideOnSinglePage: true,
})

const model = defineModel<number>({ required: true })
const pageSize = defineModel<number>('pageSize', { default: 10 })

const lastInRange = computed(() => pagesInRange.value[pagesInRange.value.length - 1])

const _pageCount = computed(() => {
  if (typeof props.pageCount === 'number') {
    return props.pageCount
  }

  return Math.ceil(props.total / pageSize.value)
})

const pagesInRange = computed(() => {
  const pages = [model.value]

  const page = model.value
  let step = 1
  while (pages.length < props.pagerCount) {
    if (page - step < 1 && page + step > _pageCount.value) {
      break
    }

    if (page - step >= 1) {
      pages.push(page - step)
    }

    if (pages.length === props.pagerCount) {
      break
    }

    if (page + step <= _pageCount.value) {
      pages.push(page + step)
    }

    step++
  }

  return pages.sort((a, b) => a - b)
})

watch(
  () => _pageCount.value,
  () => {
    if (_pageCount.value > 0 && _pageCount.value < model.value) {
      model.value = _pageCount.value
    }
  },
)

function previous() {
  if (model.value <= 1) return
  model.value--
}

function next() {
  if (model.value >= _pageCount.value) return
  model.value++
}

defineExpose({
  pagesInRange,
  lastInRange,
  _pageCount,
  previous,
  next,
})
</script>

<style>
.v-pagination,
.v-pager {
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
}

.v-table + .v-pagination {
  margin-top: 30px;
}

.v-table .v-pagination {
  margin: 20px 0;
}

.v-pager {
  list-style: none;
  padding-left: 0;
  justify-content: flex-start;
  flex-grow: 0;
  flex-shrink: 1;

  li {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 1000px;
    min-width: 30px;
    min-height: 30px;
    line-height: 30px;
    margin: 0 5px;
    padding: 0 8px;
    font-size: 14px;

    color: var(--color-primary);
    transition:
      background-color var(--transition),
      color var(--transition);
    cursor: pointer;

    &:not(.v-pagination__dots) {
      background-color: var(--color-blue-2);
    }

    &:not(.is-disabled, .is-active):hover {
      background-color: var(--color-blue-3);
    }

    &.is-active {
      background-color: var(--color-primary);
      color: #fff;
    }

    &.is-disabled {
      cursor: default;
    }

    &.v-pagination__arrow {
      transition:
        background-color var(--transition),
        stroke var(--transition);
      background-color: transparent;

      & * {
        stroke: var(--color-grey-20);
      }

      &:not(.is-disabled):hover {
        background-color: var(--color-blue-3);

        * {
          stroke: var(--color-primary);
        }
      }
    }
  }
}

.v-popper--pagination-sizes {
  margin-left: 5px;
}
</style>
